import { baseApi } from "@/services/base-api";
import type { AuthUser } from "@/store/auth/authSlice";

export interface AuthSession {
  user: AuthUser;
  accessToken: string;
}

/**
 * Endpoint paths follow docs/api/api-design.md's `/auth/register` and
 * `/auth/login` exactly, extended with the rest of Phase 2's Authentication
 * module (`logout`, `refresh`, `me`, `forgot-password`, `reset-password`).
 * `verify-otp`/`resend-otp` aren't named in that doc (it predates the
 * OTP-based verification flow the wireframes and docs/ui/routes.md's
 * `/otp-verification` route imply, superseding
 * docs/architecture/authentication.md's email-link description) — these
 * are a reasonable, documented assumption following the same
 * resource/action naming pattern, not a confirmed backend contract.
 *
 * `verifyOtp` returning a full session (auto-login on successful
 * verification) rather than requiring a separate login step afterward is a
 * deliberate UX call, not something either doc specifies either way.
 *
 * None of these pass a refresh token — it travels as an httpOnly cookie the
 * browser attaches automatically (`withCredentials: true` in
 * src/lib/axios.ts), never as a value this layer reads or writes.
 */
export const authApi = {
  register: (input: { name: string; email: string; phone: string; password: string }) =>
    baseApi.post<{ email: string }>("/auth/register", input),

  login: (input: { email: string; password: string }) =>
    baseApi.post<AuthSession>("/auth/login", input),

  verifyOtp: (input: { email: string; code: string }) =>
    baseApi.post<AuthSession>("/auth/verify-otp", input),

  resendOtp: (input: { email: string }) => baseApi.post<void>("/auth/resend-otp", input),

  /** Invalidates the refresh cookie server-side — always call this instead
   * of just discarding the access token client-side, or a stolen/lingering
   * refresh cookie would still be able to mint new access tokens. */
  logout: () => baseApi.post<void>("/auth/logout"),

  getCurrentUser: () => baseApi.get<AuthUser>("/users/me"),

  forgotPassword: (input: { email: string }) => baseApi.post<void>("/auth/forgot-password", input),

  resetPassword: (input: { token: string; password: string }) =>
    baseApi.post<void>("/auth/reset-password", input),
};
